import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { Popover, PopoverBody, PopoverContent } from "@/components/ui/popover";
import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  ReactElement,
  forwardRef,
  useState,
} from "react";
import { FlatList } from "@/components/ui/flat-list";
import { Pressable } from "@/components/ui/pressable";
import {
  LayoutChangeEvent,
  LayoutRectangle,
  ListRenderItemInfo,
  TextInput,
  View,
} from "react-native";
import { VStack } from "@/components/ui/vstack";
import { mergeRefs } from "@gluestack-ui/utils";
import { Badge, BadgeText } from "@/components/ui/badge";
import { CloseIcon, Icon } from "@/components/ui/icon";

export type AutoCompleteMenuItemType = { id: string };

export type AutoCompleteProps<
  T extends AutoCompleteMenuItemType = AutoCompleteMenuItemType
> = PropsWithChildren<{
  value: T[];
  onCreate?: (value: string) => T | undefined;
  filter?: (items: T[], value: string) => T[];
  items?: T[];
  onChange?: (value: T[]) => void;
}>;

type AutoCompleteContextType<
  T extends AutoCompleteMenuItemType = AutoCompleteMenuItemType
> = {
  external: Pick<
    AutoCompleteProps<T>,
    "items" | "onChange" | "onCreate" | "value" | "filter"
  > & {
    pushValue: (itemOrInputString: T | string) => void;
    removeValue: (idOrItem: T | string) => void;
  };
  internal: {
    ignoreFocusEvents: React.RefObject<boolean>;
    inputFocused: boolean;
    inputValue: string;
    inputCreatable: boolean;
    formattedInputValue: string;
    filteredItems: T[] | undefined;
    setInputFocused: (menuActive: boolean) => void;
    setInputValue: (inputValue: string) => void;
    setIgnoreFocusEvents: (ignoreFocusEvents: boolean) => void;
  };
};

export function AutoComplete<T extends AutoCompleteMenuItemType>({
  children,
  ...externalCtx
}: AutoCompleteProps<T>) {
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const ignoreFocusEvents = useRef(false);

  const formattedInputValue = inputValue.trim();
  const filteredItems = useMemo(() => {
    const preFilteredSelectedData = externalCtx.items?.filter(
      (i) => !externalCtx.value.find((v) => v.id === i.id)
    );

    return externalCtx.filter && preFilteredSelectedData
      ? externalCtx.filter(preFilteredSelectedData, formattedInputValue)
      : preFilteredSelectedData;
  }, [externalCtx.filter, externalCtx.items, formattedInputValue]);

  const preCreatedInputValue = useMemo(
    () => externalCtx.onCreate?.(formattedInputValue),
    [formattedInputValue, externalCtx.onCreate]
  );

  const inputCreatable = useMemo(() => {
    if (!formattedInputValue.length || !externalCtx.onCreate) {
      return false;
    }

    return (
      filteredItems?.find((i) => i.id === preCreatedInputValue?.id) ===
      undefined
    );
  }, [
    preCreatedInputValue,
    filteredItems,
    formattedInputValue,
    externalCtx.onCreate,
  ]);

  const ctx: AutoCompleteContextType<T> = {
    internal: {
      inputFocused,
      inputValue,
      formattedInputValue,
      ignoreFocusEvents,
      inputCreatable,
      filteredItems,
      setInputFocused,
      setInputValue,
      setIgnoreFocusEvents: (newValue) =>
        (ignoreFocusEvents.current = newValue),
    },
    external: {
      ...externalCtx,
      pushValue: (itemOrInputString) => {
        const item =
          typeof itemOrInputString === "string"
            ? externalCtx.onCreate?.(itemOrInputString.trim())
            : itemOrInputString;

        if (item) {
          const existing = externalCtx.value.find((v) => v.id === item.id);
          if (!existing) {
            externalCtx.onChange?.([...ctx.external.value, item]);
          }
        }
      },
      removeValue: (idOrItem) => {
        externalCtx.onChange?.(
          externalCtx.value.filter(
            typeof idOrItem === "string"
              ? (v) => v.id !== idOrItem
              : (v) => v.id !== idOrItem.id
          )
        );
      },
    },
  };

  let inputNode: ReactElement | undefined;
  let menuNode: ReactElement | undefined;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && typeof child.type !== "string") {
      if (child.type.name === AutoCompleteInput.name) {
        inputNode = child;
      } else if (child.type.name === AutoCompleteMenu.name) {
        menuNode = child;
      } else {
        throw new TypeError(
          "Elements in AutoComplete expected to be either AutoCompleteInput or AutoCompleteMenu"
        );
      }
    } else {
      throw new TypeError("Element is not react element");
    }
  });

  const inputRef = useRef<View>(null);
  const [inputSize, setInputSize] =
    useState<Pick<LayoutRectangle, "width" | "height">>();

  const isOpen = (() => {
    if (!ctx.internal.inputFocused) {
      return false;
    }

    if (ctx.internal.inputCreatable) {
      return true;
    }

    if (!!ctx.internal.filteredItems?.length) {
      return true;
    }

    return false;
  })();

  return (
    <Popover
      placement="bottom"
      shouldFlip={true}
      offset={5}
      className="p-1.5"
      isOpen={isOpen}
      onClose={() => ctx.internal.setInputFocused(false)}
      onOpen={() => ctx.internal.setInputFocused(true)}
      trapFocus={false}
      focusScope={false}
      trigger={({ ...triggerProps }) => {
        if (inputNode) {
          return React.cloneElement(inputNode, {
            triggerProps,
            ctx,
            ref: inputNode.props.ref
              ? mergeRefs([inputRef, inputNode.props.ref])
              : inputRef,
            onLayout: (e: LayoutChangeEvent) => {
              const { width, height } = e.nativeEvent.layout;

              setInputSize({ width, height });
            },
          });
        } else {
          throw new TypeError("Missing AutoCompleteInput");
        }
      }}
    >
      <PopoverContent
        style={{ width: inputSize?.width ?? 300 }}
        onStartShouldSetResponderCapture={() => {
          ctx.internal.setIgnoreFocusEvents(true);
          return false;
        }}
      >
        <PopoverBody keyboardShouldPersistTaps="handled">
          {menuNode && React.cloneElement(menuNode, { ctx })}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

export type AutoCompleteRenderItemFn<ItemT, Props = undefined> = (
  info: ListRenderItemInfo<ItemT>,
  props: Props
) => React.ReactElement | null;

export type AutoCompleteInputProps<T extends AutoCompleteMenuItemType> =
  ComponentPropsWithoutRef<typeof Input> & {
    renderItem?: AutoCompleteRenderItemFn<T, { onRemove: () => void }>;
  } & {
    triggerProps?: any;
    ctx?: AutoCompleteContextType<T>;
  };
export type AutoCompleteInputFieldProps = ComponentPropsWithoutRef<
  typeof InputField
> & {
  ctx?: AutoCompleteContextType;
};

export const AutoCompleteInput = forwardRef(function AutoCompleteInput<
  T extends AutoCompleteMenuItemType
>(
  {
    children,
    triggerProps,
    ctx,
    renderItem,
    ...rest
  }: AutoCompleteInputProps<T>,
  ref: React.ForwardedRef<any>
) {
  if (!ctx) {
    throw new TypeError("No context given for AutoCompleteInput");
  }

  const {
    onPress,
    onPressStart,
    onKeyDown,
    onKeyDownCapture,
    role,
    onBlur,
    ...filteredProps
  } = triggerProps;

  return (
    <Input
      variant="outline"
      isFullWidth
      size="md"
      isDisabled={false}
      isInvalid={false}
      isReadOnly={false}
      ref={ref}
      {...(renderItem ? { className: "h-auto min-h-10" } : undefined)}
      {...filteredProps}
      {...rest}
    >
      <VStack className="w-full h-full">
        {renderItem && ctx.external.value.length > 0 && (
          <FlatList
            className="w-full"
            scrollEnabled={false}
            contentContainerClassName="grow gap-2 px-3 pt-2"
            data={ctx.external.value}
            keyExtractor={(info) => info.id}
            renderItem={(info) => {
              return renderItem
                ? renderItem(info, {
                    onRemove: () => ctx.external.removeValue(info.item),
                  })
                : null;
            }}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            {...rest}
          />
        )}
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && typeof child.type !== "string") {
            if (child.type.name === AutoCompleteInputField.name) {
              return React.cloneElement(child, { ctx } as any);
            }
          }
          return child;
        })}
      </VStack>
    </Input>
  );
});

export const AutoCompleteInputField = forwardRef(
  function AutoCompleteInputField(
    { ctx, ...props }: AutoCompleteInputFieldProps,
    ref: React.ForwardedRef<any>
  ) {
    if (!ctx) {
      throw new TypeError("No context given for AutoCompleteInputField");
    }

    const selectedCount = useRef(ctx.external.value.length - 1);
    const inputRef = useRef<TextInput>(null);
    const mergedRef = mergeRefs([inputRef, ref]);

    useEffect(() => {
      if (selectedCount.current < ctx.external.value.length) {
        ctx.internal.setInputValue("");
      }

      return () => {
        selectedCount.current = ctx.external.value.length;
      };
    }, [ctx.external.value.length]);

    return (
      <InputField
        ref={mergedRef}
        value={ctx.internal.inputValue}
        onChangeText={(newText) => ctx.internal.setInputValue(newText)}
        onSubmitEditing={() => {
          ctx.external.pushValue(ctx.internal.formattedInputValue);
        }}
        returnKeyType="done"
        className="py-2"
        {...props}
        onFocus={() => {
          if (!ctx.internal.ignoreFocusEvents.current) {
            ctx.internal.setInputFocused(true);
          }

          props.onFocus?.();
        }}
        onBlur={() => {
          if (ctx.internal.ignoreFocusEvents.current) {
            ctx.internal.setIgnoreFocusEvents(false);
          } else {
            ctx.internal.setInputFocused(false);
          }
          props.onBlur?.();
        }}
      />
    );
  }
);

export type AutoCompleteMenuProps<T extends AutoCompleteMenuItemType> = {
  renderItem?: AutoCompleteRenderItemFn<T, { onAdd: () => void }>;
} & {
  createItem?: (
    value: string,
    props: { onAdd: () => void }
  ) => ReactElement | undefined | null;
  ctx?: AutoCompleteContextType<T>;
};

export function AutoCompleteMenu<T extends AutoCompleteMenuItemType>({
  renderItem,
  createItem,
  ctx,
  ...rest
}: AutoCompleteMenuProps<T>) {
  if (!ctx) {
    throw new TypeError("No context given for AutoCompleteMenu");
  }

  return (
    <FlatList
      className="w-full"
      scrollEnabled={false}
      contentContainerClassName="grow gap-2"
      data={ctx.internal.filteredItems}
      keyExtractor={(info) => info.id}
      renderItem={(info) => {
        return renderItem
          ? renderItem(info, { onAdd: () => ctx.external.pushValue(info.item) })
          : null;
      }}
      ListHeaderComponent={
        createItem !== undefined && ctx.internal.inputCreatable
          ? () =>
              createItem(ctx.internal.formattedInputValue, {
                onAdd: () =>
                  ctx.external.pushValue(ctx.internal.formattedInputValue),
              })
          : null
      }
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      {...rest}
    />
  );
}

export type AutoCompleteMenuInputItemProps = ComponentPropsWithoutRef<
  typeof Badge
> & {
  text: string;
  onRemove: () => void;
};

export function AutoCompleteInputItem({
  text,
  onRemove,
  ...rest
}: AutoCompleteMenuInputItemProps) {
  return (
    <Badge
      size="md"
      variant="solid"
      action="muted"
      className="justify-between px-2 py-1"
      {...rest}
    >
      <BadgeText>{text}</BadgeText>
      <Pressable className="ml-2 rounded-full" onPress={onRemove}>
        <Icon as={CloseIcon} size="sm" />
      </Pressable>
    </Badge>
  );
}

export type AutoCompleteMenuItemProps = {
  onAdd: () => void;
} & (
  | {
      text: string;
    }
  | {
      item: string;
    }
  | {
      node: ReactNode;
    }
);

export function AutoCompleteMenuItem(props: AutoCompleteMenuItemProps) {
  const finalNode =
    "text" in props ? (
      <Text>{props.text}</Text>
    ) : "item" in props ? (
      <Text>{`Add ${props.item}`}</Text>
    ) : typeof props.node === "string" ? (
      <Text>{props.node}</Text>
    ) : (
      props.node
    );

  return (
    <Pressable className="w-full" onPress={props.onAdd}>
      {finalNode}
    </Pressable>
  );
}

// Redecalare forwardRef
// to support generic on HOC
declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactNode | null
  ): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}
